import { WizardFormData } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { CustomerTypeValue, FlagType } from '@/types';

export const DEFAULT_FORM_DATA: WizardFormData = {
  settings_display_name: '',
  profile_picture: undefined,
  customer_type: null,
  subscription_start_date: '',
  subscription_end_date: '',
  subscription_cost: undefined,
  subscription_msrp: undefined,
  subscription_assets: undefined,
  subscription_applications: undefined,
  attack_surface: {
    external: false,
    applications: false,
    internal: false,
    cloud: false,
    code: false,
    users: false,
    device: false,
    llm: false,
  },
  plextrac_id: undefined,
  hackerone_program_url: undefined,
  collaborators: [],
  parent_account: undefined,
  allowed_domains: [],
};

export const WIZARD_STEPS = {
  BASIC_INFO: 1,
  ATTACK_SURFACE: 2,
  CONFIGURATION: 3,
  SUBSCRIPTION_PRICING: 4, // New step for MANAGED/SAAS
  PRAETORIAN_USERS: 5, // Was 4, now 5
  CUSTOMER_USERS: 6, // Was 5, now 6
};

export const TOTAL_STEPS = 6; // Maximum number of steps (MANAGED/SAAS see all, others skip step 4)

export const STEP_TITLES: { [key: number]: string } = {
  [WIZARD_STEPS.BASIC_INFO]: 'Basic Information',
  [WIZARD_STEPS.ATTACK_SURFACE]: 'Attack Surface',
  [WIZARD_STEPS.CONFIGURATION]: 'Configuration',
  [WIZARD_STEPS.SUBSCRIPTION_PRICING]: 'Subscription Pricing',
  [WIZARD_STEPS.PRAETORIAN_USERS]: 'Praetorian Users',
  [WIZARD_STEPS.CUSTOMER_USERS]: 'Customer Users',
};

// Dynamic total steps based on customer type
// MANAGED/SAAS/BUG_BOUNTY see all 6 steps, others skip SUBSCRIPTION_PRICING (step 4) so see 5 steps
export const getTotalSteps = (
  customerType: CustomerTypeValue | null
): number => {
  // MANAGED, SAAS, and BUG_BOUNTY customers see all steps including subscription pricing
  if (
    customerType === 'MANAGED' ||
    customerType === 'SAAS' ||
    customerType === 'BUG_BOUNTY'
  ) {
    return 6;
  }
  // Other customer types skip the subscription pricing step
  return 5;
};

// Get the display step number accounting for skipped steps
// For non-MANAGED/SAAS/BUG_BOUNTY customers, step 4 is skipped, so step 5 displays as 4, step 6 as 5
export const getDisplayStep = (
  actualStep: number,
  customerType: CustomerTypeValue | null
): number => {
  // MANAGED, SAAS, and BUG_BOUNTY customers see all steps, so display = actual
  if (
    customerType === 'MANAGED' ||
    customerType === 'SAAS' ||
    customerType === 'BUG_BOUNTY'
  ) {
    return actualStep;
  }

  // For other types, step 4 is skipped
  // If we're past step 4, subtract 1 from the display
  if (actualStep > WIZARD_STEPS.SUBSCRIPTION_PRICING) {
    return actualStep - 1;
  }

  return actualStep;
};

export const DEFAULT_FLAGS = [
  FlagType.ENABLE_AI_AUTOMATION,
  FlagType.DISABLE_STATIC_IP_ADDRESSES,
];

export const PILOT_DURATION_BUSINESS_DAYS = 20;

export const MINIMUM_SUBSCRIPTION_COST = 0;

export const SUBSCRIPTION_ELIGIBLE_TYPES = [
  'FREEMIUM',
  'PILOT',
  'ENGAGEMENT',
  'MANAGED',
  'SAAS',
  'BUG_BOUNTY',
] as const;

export const FREEMIUM_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: 'freemium',
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'weekly',
} as const;

export const PILOT_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: null,
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'daily',
} as const;

export const ENGAGEMENT_SUBSCRIPTION_CONFIG = {
  estimatedCost: 0,
  msrp: 0,
  selectedTier: null,
  numberOfAssets: 0,
  numberOfApplications: 0,
  scanSchedule: 'daily',
} as const;

export const MANAGED_SUBSCRIPTION_CONFIG = {
  selectedTier: 'ultimate',
  scanSchedule: 'daily',
} as const;

export const SAAS_SUBSCRIPTION_CONFIG = {
  selectedTier: 'foundational',
  scanSchedule: 'daily',
} as const;

export const BUG_BOUNTY_SUBSCRIPTION_CONFIG = {
  selectedTier: 'ultimate',
  scanSchedule: 'daily',
} as const;

/**
 * Get default form data for a specific customer type.
 * Returns type-aware defaults that include auto-initialized values to prevent
 * false positives in hasUserSetValues() comparison.
 *
 * MANAGED and SAAS types auto-initialize subscription fields, so their defaults
 * include those pre-set values. Other types use base defaults.
 *
 * @param type - Customer type (or null for base defaults)
 * @returns WizardFormData with appropriate defaults for the type
 */
export const getDefaultsForType = (
  type: CustomerTypeValue | null
): WizardFormData => {
  const baseDefaults = { ...DEFAULT_FORM_DATA };

  // MANAGED type: Apply auto-initialized subscription defaults
  if (type === 'MANAGED') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: MANAGED_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: MANAGED_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
    };
  }

  // SAAS type: Apply auto-initialized subscription defaults
  if (type === 'SAAS') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: SAAS_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: SAAS_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
    };
  }

  // BUG_BOUNTY type: Apply auto-initialized subscription defaults (same as MANAGED)
  if (type === 'BUG_BOUNTY') {
    return {
      ...baseDefaults,
      customer_type: type,
      subscription_tier: BUG_BOUNTY_SUBSCRIPTION_CONFIG.selectedTier,
      subscription_scan_schedule: BUG_BOUNTY_SUBSCRIPTION_CONFIG.scanSchedule,
      subscription_assets: 0,
      subscription_applications: 0,
    };
  }

  // All other types (ENGAGEMENT, PILOT, FREEMIUM): Return base defaults with type set
  return {
    ...baseDefaults,
    customer_type: type,
  };
};
